// Word-level diff between the raw dictation and the tidied version, so the
// clinician can see what the AI changed instead of re-reading the whole note.
//
// Returns the two texts interleaved as segments — unchanged, added (from the
// tidied text), and removed (from the original) — plus a count of distinct
// changes. Rendering only the unchanged and added segments gives the tidied
// text; rendering all three gives a track-changes view.

const TOKEN_RE = /\S+\s*/g;

// Words are compared case-insensitively but punctuation is significant, so an
// added comma or full stop is shown as a change. Capitalisation alone is not,
// because the tidy step recapitalises every sentence and that would drown the
// changes that matter.
const normalise = (token) => token.trim().toLowerCase();

const tokenise = (text) => (text || "").match(TOKEN_RE) || [];

// An O(m*n) table is fine for a dictation but not for an essay. Past this many
// cells the diff is skipped and the text is returned unmarked.
const MAX_CELLS = 4000000;

export function diffWords(original, enhanced) {
  const enhancedTokens = tokenise(enhanced);

  if (!original || !original.trim()) {
    return {
      segments: [{ text: enhanced || "", added: false, removed: false }],
      changeCount: 0,
    };
  }

  const originalTokens = tokenise(original);
  const m = originalTokens.length;
  const n = enhancedTokens.length;

  if (m * n > MAX_CELLS) {
    return {
      segments: [{ text: enhanced || "", added: false, removed: false }],
      changeCount: 0,
    };
  }

  const a = originalTokens.map(normalise);
  const b = enhancedTokens.map(normalise);

  // Longest common subsequence over the normalised words.
  const width = n + 1;
  const lcs = new Int32Array((m + 1) * width);
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * width + (j + 1)] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + (j + 1)]);
    }
  }

  // Walk the table, interleaving kept, added, and removed words. An adjacent
  // removal + insertion (a replacement) counts as one change.
  const segments = [];
  let changeCount = 0;
  let lastWasChange = false;
  let i = 0;
  let j = 0;

  const push = (text, added, removed) => {
    const previous = segments[segments.length - 1];
    if (previous && previous.added === added && previous.removed === removed) {
      previous.text += text;
    } else {
      segments.push({ text, added, removed });
    }
  };

  const pushRemoved = () => {
    // Dropped words keep their trailing space so the struck-out run reads as
    // whole words, not letters jammed together.
    const token = originalTokens[i];
    push(token.trimEnd() + " ", false, true);
    if (!lastWasChange) changeCount += 1;
    lastWasChange = true;
    i += 1;
  };

  const pushAdded = () => {
    push(enhancedTokens[j], true, false);
    if (!lastWasChange) changeCount += 1;
    lastWasChange = true;
    j += 1;
  };

  while (i < m && j < n) {
    if (a[i] === b[j]) {
      push(enhancedTokens[j], false, false);
      lastWasChange = false;
      i += 1;
      j += 1;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + (j + 1)]) {
      pushRemoved();
    } else {
      pushAdded();
    }
  }

  while (i < m) pushRemoved();
  while (j < n) pushAdded();

  return { segments, changeCount };
}

export default diffWords;
